// Noyau — lexeur + parseur de Pratt + imprimeurs, PILOTÉS PAR UNE TABLE.
// Aucun opérateur, aucune fonction, aucun type de nœud n'est écrit ici :
// tout vient de la SyntaxTable produite par le compilateur à partir des packs.
// Le noyau ne fournit que des STRATÉGIES génériques (infix, prefix, postfix,
// implicite, appel de fonction ; impression sum / product / power / template…).

import { num, sym, pvar, node, isLeaf, equal } from './ast.js';

export class ParseError extends Error {
  constructor(msg, pos, len = 1) {
    super(msg);
    this.pos = pos;
    this.len = len;
  }
}

// ── Lexeur ────────────────────────────────────────────────────────────────
// Jetons : { k: 'num'|'id'|'sym'|'name'|'var'|'end', v, pos, len, ws }
//   sym  = symbole d'opérateur ou de groupe (clé dans table.lex.symbols)
//   name = fonction, constante ou mot-opérateur connu de la table ("sin", "π", "ou")
//   ws   = un blanc précède le jeton
export function tokenize(src, table, opts = {}) {
  const T = table.lex;
  const toks = [];
  const letter = new RegExp(`^[${T.letters}]`, 'u');
  const subs = T.subscripts || '';
  const symList = T.symbolList; // trié par longueur décroissante
  let i = 0,
    ws = false;
  while (i < src.length) {
    const c = src[i];
    if (/\s/.test(c) || c === ' ' || c === ' ') {
      i++;
      ws = true;
      continue;
    }
    const start = i;
    // variable de motif : ?a, ?k…
    if (c === '?' && opts.patternVars) {
      const m = /^\?([A-Za-z][A-Za-z0-9_]*)/.exec(src.slice(i));
      if (m) {
        toks.push({ k: 'var', v: m[1], pos: i, len: m[0].length, ws });
        i += m[0].length;
        ws = false;
        continue;
      }
    }
    // nombre : 12, 0,75, 3.5 (séparateur décimal seulement entre deux chiffres)
    if (/[0-9]/.test(c)) {
      const re = new RegExp(`^[0-9]+(?:[${escapeClass(T.decimal.join(''))}][0-9]+)?`);
      const m = re.exec(src.slice(i));
      // vecteur nul « 0⃗ » (marque de vecteur déclarée par un pack)
      if (T.vectorMark && m[0] === '0' && src.startsWith(T.vectorMark, i + 1)) {
        toks.push({ k: 'id', v: '0' + T.vectorMark, pos: i, len: 1 + T.vectorMark.length, ws });
        i += 1 + T.vectorMark.length;
        ws = false;
        continue;
      }
      toks.push({ k: 'num', v: m[0], pos: i, len: m[0].length, ws });
      i += m[0].length;
      ws = false;
      continue;
    }
    // l'infini (pack d'analyse) : « ∞ » et « +∞ » en tête d'expression sont la constante +∞ ; « −∞ » est son opposé
    if (T.infinity) {
      const prev = toks[toks.length - 1];
      const afterValue =
        prev &&
        (prev.k === 'num' ||
          prev.k === 'id' ||
          (prev.k === 'name' && T.names[prev.v] === 'constant') ||
          (prev.k === 'sym' && String(prev.v).startsWith('close:')));
      const plus = !afterValue && c === '+' && src.startsWith(T.infinity, i + 1);
      if (plus || src.startsWith(T.infinity, i)) {
        const len = (plus ? 1 : 0) + T.infinity.length;
        toks.push({ k: 'name', v: '+' + T.infinity, pos: i, len, ws });
        i += len;
        ws = false;
        continue;
      }
    }
    // exposant écrit en chiffres exposants (lexique `superscripts`, pack core) : « x⁵ », « 10⁻³ », « 2ⁿ » → UN jeton
    // « sup » lu comme un exposant postfixe. Un seul signe déjà déclaré comme symbole (« ² », « ³ », « 0⁻ » des
    // limites) garde son sens historique.
    if (T.superscripts && T.superscripts[c] !== undefined && afterValue(toks, T)) {
      let j = i,
        txt = '';
      while (j < src.length && T.superscripts[src[j]] !== undefined) {
        txt += T.superscripts[src[j]];
        j++;
      }
      const single = j - i === 1 && T.symbols[c];
      if (!single) {
        if (!/^−?(?:[0-9]+|[A-Za-z])$/.test(txt))
          throw new ParseError(`Je ne sais pas lire l’exposant « ${src.slice(i, j)} ».`, i, j - i);
        toks.push({ k: 'sup', v: txt, pos: i, len: j - i, ws });
        i = j;
        ws = false;
        continue;
      }
    }
    // nom de plusieurs lettres déclaré par l'exercice (« AC », « Ec », « CMₘ », « P(A∩B) », « f(2) ») : UN identifiant,
    // seulement s'il n'est pas collé à d'autres lettres (« BAC » ne contient pas « AC »)
    if (
      T.modelIds?.length &&
      !(i > 0 && letter.test(String.fromCodePoint(src.codePointAt(i - 1))))
    ) {
      const id = T.modelIds.find(
        (n) => src.startsWith(n, i) && !continuesName(src, i + n.length, T, letter),
      );
      if (id) {
        toks.push({ k: 'id', v: id, pos: i, len: id.length, ws });
        i += id.length;
        ws = false;
        continue;
      }
    }
    // symboles (les plus longs d'abord : "<=" avant "<")
    let hit = null;
    for (const s of symList)
      if (src.startsWith(s, i)) {
        hit = s;
        break;
      }
    if (hit && !letter.test(hit)) {
      toks.push({ k: 'sym', v: T.symbols[hit], raw: hit, pos: i, len: hit.length, ws });
      i += hit.length;
      ws = false;
      continue;
    }
    // formule d'atomes déclarés par un pack (« H₂O », « NaCl ») : un seul identifiant
    if (T.formulaAtoms?.length) {
      const end = readFormula(src, i, T, letter);
      if (end > 0 && !(T.vectorMark && src.startsWith(T.vectorMark, end))) {
        // « BC⃗ » est un vecteur, pas une formule
        toks.push({ k: 'id', v: src.slice(i, end), pos: i, len: end - i, ws });
        i = end;
        ws = false;
        continue;
      }
    }
    if (letter.test(src.slice(i))) {
      // suite maximale de lettres
      let j = i;
      while (j < src.length) {
        const ch = String.fromCodePoint(src.codePointAt(j));
        if (!letter.test(ch)) break;
        j += ch.length;
      }
      const run = src.slice(i, j);
      // « f′(x) », « f″(x) » : nom de la dérivée d'une fonction (marque de dérivée déclarée par un pack) → UN identifiant
      if (T.prime && run.length === 1 && src.startsWith(T.prime, j)) {
        let k = j;
        while (src.startsWith(T.prime, k)) k += T.prime.length;
        const arg = new RegExp(`^\\([${T.letters}]\\)`, 'u').exec(src.slice(k));
        const end = arg ? k + arg[0].length : k;
        toks.push({ k: 'id', v: src.slice(i, end), pos: i, len: end - i, ws });
        i = end;
        ws = false;
        continue;
      }
      // vecteur « AB⃗ », « u⃗ » : lettres suivies de la marque de vecteur (pack) → UN identifiant
      if (T.vectorMark && src.startsWith(T.vectorMark, j)) {
        toks.push({ k: 'id', v: run + T.vectorMark, pos: i, len: j - i + T.vectorMark.length, ws });
        i = j + T.vectorMark.length;
        ws = false;
        continue;
      }
      // mot entier connu : fonction, constante, mot-opérateur
      // une fonction d'une seule lettre (« C(4 ; 2) ») n'est une fonction que suivie de « ( » : sinon C reste une variable
      const fnOneLetter = run.length === 1 && T.names[run] === 'function' && src[j] !== '(';
      if (T.names[run] && !fnOneLetter) {
        toks.push({ k: 'name', v: run, pos: i, len: run.length, ws });
        i = j;
        ws = false;
        continue;
      }
      // préfixe connu suivi d'une parenthèse : "sin(" dans "2sin(x)" est déjà séparé ; "xsin(" rare → on découpe
      let k = i;
      while (k < j) {
        let matched = null;
        for (const n of T.nameList)
          if (
            src.startsWith(n, k) &&
            k + n.length <= j &&
            T.names[n] !== 'word' &&
            !(n.length === 1 && T.names[n] === 'function' && src[k + 1] !== '(')
          ) {
            matched = n;
            break;
          }
        if (matched) {
          toks.push({
            k: 'name',
            v: matched,
            pos: k,
            len: matched.length,
            ws: k === i ? ws : false,
          });
          k += matched.length;
          continue;
        }
        const ch = String.fromCodePoint(src.codePointAt(k));
        let end = k + ch.length,
          name = ch;
        // indices, primes et signes diacritiques collés : v₀, x', Â (A + accent), x̄
        while (
          end < src.length &&
          (subs.includes(src[end]) || src[end] === "'" || (T.combining || '').includes(src[end]))
        ) {
          name += src[end];
          end++;
        }
        if (end > j) j = end;
        toks.push({ k: 'id', v: name, pos: k, len: end - k, ws: k === i ? ws : false });
        k = end;
      }
      i = j;
      ws = false;
      continue;
    }
    throw new ParseError(`Je ne connais pas le signe « ${c} ».`, i);
  }
  toks.push({ k: 'end', pos: src.length, len: 0, ws });
  return toks;
}

// Le jeton précédent est une valeur (un exposant peut suivre) : nombre, identifiant, constante, fermeture, exposant.
function afterValue(toks, T) {
  const prev = toks[toks.length - 1];
  return (
    !!prev &&
    (prev.k === 'num' ||
      prev.k === 'id' ||
      prev.k === 'sup' ||
      (prev.k === 'name' && T.names[prev.v] === 'constant') ||
      (prev.k === 'sym' && String(prev.v).startsWith('close:')))
  );
}

// Une lettre, un indice, une prime ou un signe diacritique prolonge le nom : « AC » n'est pas le début de « ACD ».
function continuesName(src, j, T, letter) {
  if (j >= src.length) return false;
  const ch = String.fromCodePoint(src.codePointAt(j));
  return (
    letter.test(ch) ||
    (T.subscripts || '').includes(ch) ||
    (T.combining || '').includes(ch) ||
    ch === "'" ||
    ch === T.prime
  );
}

// Lit une formule d'atomes (table.lex.formulaAtoms, triés du plus long au plus court) à partir de i.
// Renvoie l'indice de fin, ou -1 si ce n'est pas une formule « utile » : il faut au moins deux atomes,
// ou un atome de plusieurs lettres, et pas de lettre collée après (sinon on retombe sur le cas général).
export function readFormula(src, i, T, letter) {
  const subs = T.subscripts || '';
  let j = i,
    atoms = 0,
    multi = false;
  for (;;) {
    const a = T.formulaAtoms.find((x) => src.startsWith(x, j));
    if (!a) break;
    j += a.length;
    atoms++;
    if (a.length > 1) multi = true;
    while (j < src.length && subs.includes(src[j])) j++;
  }
  if (!atoms || (atoms < 2 && !multi)) return -1;
  if (j < src.length && letter.test(String.fromCodePoint(src.codePointAt(j)))) return -1;
  return j;
}

function escapeClass(s) {
  return s.replace(/[\\\][^-]/g, '\\$&');
}

// ── Parseur de Pratt ──────────────────────────────────────────────────────
export function parse(src, table, opts = {}) {
  const toks = tokenize(src, table, opts);
  const S = table.syntax;
  let p = 0;
  const peek = () => toks[p];
  const next = () => toks[p++];

  const infixOf = (t) => {
    if (t.k === 'sym' && S.infix[t.v]) return S.infix[t.v];
    if (t.k === 'name' && S.words[t.v] && S.infix[S.words[t.v]]) return S.infix[S.words[t.v]];
    return null;
  };
  const supOf = (t) => {
    const neg = t.v.startsWith('−');
    const body = neg ? t.v.slice(1) : t.v;
    const e = /^[0-9]+$/.test(body) ? num(parseInt(body, 10)) : sym(body);
    return { node: table.lex.superscriptNode ?? 'Pow', prec: 30, arg: neg ? node('Neg', [e]) : e };
  };
  let absOpen = 0;
  const postfixOf = (t) => (t.k === 'sup' ? supOf(t) : (t.k === 'sym' && S.postfix[t.v]) || null);
  const startsPrimary = (t) =>
    t.k === 'num' ||
    t.k === 'id' ||
    t.k === 'var' ||
    (t.k === 'name' && !S.words[t.v]) ||
    (t.k === 'sym' &&
      ((S.groups[t.v] && !(absOpen > 0 && S.groups[t.v] === t.v)) || S.prefixPrimary[t.v]));

  // relations d'ordre et connecteur « et » de la table (encadrements)
  const ORDER = new Set(['lt', 'le', 'gt', 'ge']);
  const isOrder = (t) => ORDER.has(table.nodes[t]?.relation);
  const AND = Object.entries(table.nodes).find(([, sp]) => sp.connective === 'and')?.[0];

  function expr(minBp) {
    let left = prefix();
    for (;;) {
      const t = peek();
      const post = postfixOf(t);
      if (post && post.prec >= minBp) {
        next();
        left = node(post.node, post.arg ? [left, post.arg] : [left]);
        continue;
      }
      const inf = infixOf(t);
      if (inf) {
        if (inf.prec < minBp) break;
        if (inf.assoc === 'none' && minBp === inf.prec + 0.5 && opts._noChain) break;
        next();
        const rbp = inf.assoc === 'right' ? inf.prec : inf.prec + 0.5;
        let right = expr(rbp);
        if (inf.wrapRight) right = node(inf.wrapRight, [right]);
        if (inf.chain && left.t === inf.node && !left._closed) {
          // relations chaînées « a = b = c » : on garde la chaîne à plat
          left = { ...left, k: [...left.k, right] };
        } else if (isOrder(inf.node) && !left._closed && (isOrder(left.t) || left._ineqChain)) {
          // encadrement « a < b ≤ c » : « a < b et b ≤ c »
          const last = left._ineqChain ? left.k.at(-1) : left;
          const pair = node(inf.node, [last.k.at(-1), right]);
          left = left._ineqChain ? { ...left, k: [...left.k, pair] } : node(AND, [left, pair]);
          Object.defineProperty(left, '_ineqChain', { value: true, enumerable: false });
        } else left = node(inf.node, [left, right]);
        continue;
      }
      // multiplication implicite : « 2x », « (x+1)(x−1) », « 2sin(x) », « a t »
      if (S.implicit && startsPrimary(t) && S.implicit.prec >= minBp) {
        const rhs = expr(S.implicit.prec + 0.5);
        left = node(S.implicit.node, [left, rhs]);
        continue;
      }
      break;
    }
    return left;
  }

  function prefix() {
    const t = next();
    switch (t.k) {
      case 'num': {
        const sep = table.lex.decimal.find((d) => t.v.includes(d));
        if (!sep) {
          // entier écrit de plus de 15 chiffres : gardé exact (« 10²⁴ = 1000…000 »)
          const n = num(parseInt(t.v, 10));
          return /^\d{16,}$/.test(t.v) ? { ...n, big: t.v.replace(/^0+(?=\d)/, '') } : n;
        }
        const [a, b] = t.v.split(sep);
        const q = 10 ** b.length;
        return { ...num(parseInt(a, 10) * q + parseInt(b, 10), q), dec: t.v };
      }
      case 'id':
        return sym(t.v);
      case 'var':
        return pvar(t.v);
      case 'name': {
        const f = S.functions[t.v];
        if (f) {
          const open = peek();
          if (open.k === 'sym' && S.groups[open.v]) {
            next();
            const args = [expr(0)];
            while (peek().k === 'sym' && peek().v === S.argSep) {
              next();
              args.push(expr(0));
            }
            expect(S.groups[open.v]);
            return node(f.node, args, { name: t.v });
          }
          return node(f.node, [expr(f.prec ?? 25)], { name: t.v });
        }
        if (S.constants[t.v]) return sym(t.v);
        throw new ParseError(`« ${t.v} » ne peut pas commencer une expression.`, t.pos, t.len);
      }
      case 'sym': {
        if (S.groups[t.v]) {
          // même signe pour fermer (« |…| ») : à l'intérieur, ce signe ferme et n'ouvre pas
          const self = S.groups[t.v] === t.v;
          if (self) absOpen++;
          let inner;
          try {
            inner = expr(0);
            expect(S.groups[t.v]);
          } finally {
            if (self) absOpen--;
          }
          const gn = S.groupNodes?.[t.v];
          return gn ? node(gn, [inner]) : { ...inner, _closed: true };
        }
        const pre = S.prefix[t.v];
        if (pre) return node(pre.node, [expr(pre.prec)]);
        // « + » devant un nombre (« (+7) ») : le nombre lui-même ; même priorité que le « − » d'un opposé
        if (t.raw === '+' || t.v === '+') {
          const neg = Object.values(S.prefix)[0];
          return expr(neg?.prec ?? 25);
        }
        throw new ParseError(`Il manque quelque chose avant « ${t.raw} ».`, t.pos, t.len);
      }
      case 'end':
        throw new ParseError('La ligne s’arrête trop tôt.', t.pos);
    }
    throw new ParseError('Écriture inattendue.', t.pos, t.len);
  }

  function expect(closeId) {
    const t = next();
    if (!(t.k === 'sym' && t.v === closeId))
      throw new ParseError('Une parenthèse n’est pas fermée.', t.pos, t.len);
  }

  const ast = expr(0);
  if (peek().k !== 'end') {
    const t = peek();
    throw new ParseError(
      `Je ne comprends pas la suite à partir de « ${src.slice(t.pos, t.pos + Math.max(1, t.len))} ».`,
      t.pos,
      t.len,
    );
  }
  return strip(ast);
}

function strip(n) {
  if (isLeaf(n)) return n;
  const { _closed, ...rest } = n;
  return { ...rest, k: n.k.map(strip) };
}

// ── Imprimeurs ────────────────────────────────────────────────────────────
// Chaque type de nœud porte dans la table : { prec, print: stratégie, text, latex }.
// Stratégies : 'sum' (Add avec Neg → « − »), 'product' (juxtaposition quand c'est lisible),
// 'power' (² ³ en texte, ^{} en LaTeX), 'infix' (symbole fixe, relations, ou/et),
// 'prefix' (−a), 'template' ($0, $1 dans text/latex), 'call' (f(x)).
export function toText(n, table) {
  return print(n, table, 'text');
}
export function toLatex(n, table) {
  return print(n, table, 'latex');
}

const SUP = {
  0: '⁰',
  1: '¹',
  2: '²',
  3: '³',
  4: '⁴',
  5: '⁵',
  6: '⁶',
  7: '⁷',
  8: '⁸',
  9: '⁹',
  '-': '⁻',
};
const SUB_DIGITS = '₀₁₂₃₄₅₆₇₈₉';

function precOf(n, table) {
  if (n.t === 'Num')
    return n.p < 0
      ? (table.nodes.Neg?.prec ?? 25)
      : n.q !== 1 && !n.dec
        ? (table.nodes.Div?.prec ?? 20)
        : 100;
  if (n.t === 'Sym' || n.t === 'Var') return 100;
  return table.nodes[n.t]?.prec ?? 100;
}

// « et » de relations d'ordre qui se suivent (le dernier membre de l'une est le premier de la suivante)
function chainOf(n, table) {
  const order = (t) => ['lt', 'le', 'gt', 'ge'].includes(table.nodes[t]?.relation);
  if (n.k.length < 2 || !n.k.every((c) => order(c.t) && c.k.length === 2)) return null;
  for (let i = 1; i < n.k.length; i++) if (!equal(n.k[i - 1].k[1], n.k[i].k[0])) return null;
  return { ops: n.k.map((c) => c.t), terms: [n.k[0].k[0], ...n.k.map((c) => c.k[1])] };
}

function print(n, table, mode, parentPrec = 0, side = 'l') {
  const L = mode === 'latex';
  const wrap = (s, need) => (need ? (L ? `\\left(${s}\\right)` : `(${s})`) : s);
  switch (n.t) {
    case 'Num': {
      let s;
      if (n.dec) s = L ? n.dec.replace(',', '{,}') : n.dec;
      else if (n.q === 1) s = String(Math.abs(n.p));
      else s = L ? `\\dfrac{${Math.abs(n.p)}}{${n.q}}` : `${Math.abs(n.p)}/${n.q}`;
      if (n.dec && n.p < 0) s = s.replace('-', '');
      if (n.p < 0) s = (L ? '-' : '−') + s;
      return wrap(s, n.p < 0 && parentPrec > (table.nodes.Neg?.prec ?? 25) - 1);
    }
    case 'Sym':
      return L ? symLatex(n.name, table) : n.name;
    case 'Var':
      return L ? `\\mathit{?${n.name}}` : `?${n.name}`;
  }
  const spec = table.nodes[n.t];
  if (!spec) throw new Error(`type de nœud inconnu : ${n.t}`);
  const prec = spec.prec ?? 100;
  const kid = (c, s = 'l', pp = prec) => print(c, table, mode, pp, s);
  // encadrement « a < b et b ≤ c » : réécrit « a < b ≤ c », comme l'élève l'a tapé
  const enc = spec.connective === 'and' ? chainOf(n, table) : null;
  if (enc) {
    const rel = table.nodes[enc.ops[0]];
    let s = kid(enc.terms[0], 'l', (rel.prec ?? 100) + 0.5);
    enc.ops.forEach((op, i) => {
      const sp = table.nodes[op];
      s +=
        ((L ? sp.latex : sp.text) ?? ` ${op} `) +
        kid(enc.terms[i + 1], 'r', (sp.prec ?? 100) + 0.5);
    });
    return wrap(s, prec < parentPrec);
  }
  let out;
  switch (spec.print) {
    case 'sum': {
      out = n.k
        .map((c, i) => {
          if (i === 0) return kid(c, 'l', prec);
          if (c.t === (spec.negNode || 'Neg'))
            return (L ? ' - ' : ' − ') + unplus(kid(c.k[0], 'r', prec + 0.5), table);
          if (c.t === 'Num' && c.p < 0)
            return (
              (L ? ' - ' : ' − ') +
              kid({ ...c, p: -c.p, dec: c.dec?.replace('-', '') }, 'r', prec + 0.5)
            );
          return ' + ' + unplus(kid(c, 'r', prec), table);
        })
        .join('');
      break;
    }
    case 'product': {
      const negLike = (c) => c.t === 'Neg' || (c.t === 'Num' && c.p < 0);
      const parts = n.k.map((c, i) =>
        i && negLike(c) ? wrap(print(c, table, mode, 0), true) : kid(c, i ? 'r' : 'l', prec),
      );
      out = parts[0];
      for (let i = 1; i < n.k.length; i++) {
        const a = n.k[i - 1],
          b = n.k[i];
        const juxta = juxtaposable(a, b, table);
        out += juxta
          ? L
            ? ' '
            : a.t === 'Sym' && b.t === 'Sym'
              ? ' '
              : ''
          : L
            ? ' \\times '
            : ' × ';
        out += parts[i];
      }
      break;
    }
    case 'power': {
      const [b, e] = n.k;
      // un appel garde ses parenthèses : sin(x)², pas (sin(x))²
      const base =
        table.nodes[b.t]?.print === 'call' ? print(b, table, mode, 0) : kid(b, 'l', prec + 0.5);
      if (L) out = `${base}^{${print(e, table, mode, 0)}}`;
      // en exposant seulement ce que le parseur relit (² et ³, déclarés par core) ; sinon « ^4 » : l'écriture se relit
      else if (e.t === 'Num' && e.q === 1 && !e.dec && (e.p === 2 || e.p === 3))
        out = base + SUP[e.p];
      else if (e.t === 'Sym' || e.t === 'Num') out = `${base}^${print(e, table, mode, 100)}`;
      else out = `${base}^(${print(e, table, mode, 0)})`;
      break;
    }
    case 'prefix':
      out =
        (L ? spec.latex : spec.text) + unplus(kid(n.k[0], 'r', spec.operandPrec ?? prec), table);
      break;
    case 'infix':
      out = n.k
        .map((c, i) => kid(c, i ? 'r' : 'l', prec + (i ? 0.5 : 0)))
        .join(L ? spec.latex : spec.text);
      break;
    case 'call': {
      const f = table.syntax.functions[n.name] || {};
      const head = L ? (f.latex ?? `\\operatorname{${n.name}}`) : n.name;
      out = `${head}${L ? '\\left(' : '('}${n.k.map((c) => print(c, table, mode, 0)).join(L ? ',\\,' : ' ; ')}${L ? '\\right)' : ')'}`;
      break;
    }
    case 'template':
    default: {
      const tpl = L ? spec.latex : spec.text;
      out = tpl.replace(/\$(\d)/g, (m, d, off) => {
        const c = n.k[+d];
        const before = tpl[off - 1],
          after = tpl[off + 2];
        const delimited = (before === '{' && after === '}') || (before === '(' && after === ')');
        return delimited
          ? print(c, table, mode, 0)
          : kid(c, +d ? 'r' : 'l', +d ? prec + 0.5 : prec);
      });
    }
  }
  return wrap(
    out,
    prec < parentPrec ||
      (prec === parentPrec &&
        side === 'r' &&
        spec.print !== 'sum' &&
        spec.print !== 'product' &&
        !spec.assoc),
  );
}

// après « + » ou « − », l'infini s'écrit sans son signe : « 3 − ∞ », « −∞ » (pas « 3 − +∞ »)
function unplus(s, table) {
  const inf = table.lex.infinity;
  if (!inf) return s;
  if (s.startsWith('+' + inf)) return s.slice(1);
  if (s.startsWith('+\\infty')) return s.slice(1);
  return s;
}

function juxtaposable(a, b, table) {
  // « 2x », « 3(x+1) », « x(x−4) », « 2sin(x) », « a t » ; jamais « 2 × 3 », « x × 2 »
  if (b.t === 'Num' || b.t === 'Neg' || b.t === 'Div') return false;
  const leftOk = (a.t === 'Num' && a.p > 0 && !a.dec && a.q === 1) || a.t === 'Sym';
  if (!leftOk) return false;
  if (b.t === 'Sym') return /^\p{L}/u.test(b.name) && !(a.t === 'Sym' && a.name === b.name); // pas « 3+∞ »
  if (b.t === 'Call') return true;
  if (b.t === 'Pow') return b.k[0].t === 'Sym' || b.k[0].t === 'Call';
  return precOf(b, table) < (table.nodes.Mul?.prec ?? 20); // sera parenthésé : « 2(x + 1) »
}

function symLatex(name, table) {
  const c = table.syntax.constants[name];
  if (c?.latex) return c.latex;
  const T = table.lex;
  if (T.formulaAtoms?.length && T.formulaAtoms.some((a) => name.startsWith(a))) {
    // formule (« H₂O ») : en romain, indices en _{…}
    let j = 0;
    for (;;) {
      const a = T.formulaAtoms.find((x) => name.startsWith(x, j));
      if (!a) break;
      j += a.length;
      while (j < name.length && SUB_DIGITS.includes(name[j])) j++;
    }
    if (j === name.length)
      return `\\mathrm{${name.replace(/[₀-₉]+/gu, (m) => `_{${[...m].map((d) => SUB_DIGITS.indexOf(d)).join('')}}`)}}`;
  }
  const m = /^(.+?)([₀-₉ₙₖₚ₊₋]+)?('*)$/u.exec(name);
  const greek = table.lex.greekLatex?.[m[1]];
  let s = greek ?? (m[1].length > 1 ? `\\mathrm{${m[1]}}` : m[1]);
  const SUBL = { ₙ: 'n', ₖ: 'k', ₚ: 'p', '₊': '+', '₋': '-' };
  if (m[2]) s += `_{${[...m[2]].map((d) => SUBL[d] ?? SUB_DIGITS.indexOf(d)).join('')}}`;
  if (m[3]) s += m[3];
  return s;
}

// Fonctions ABSTRAITES d'un exercice (f, g, u…, lot E2) : une table dérivée où ces noms sont des fonctions
// « non interprétées » (eval: 'abstract') — « f(x) » devient un appel, plus jamais le produit f × x. Le juge les
// évalue en tirant une fonction au hasard à chaque échantillon (equiv.js). Mise en cache par table et par noms.
const WITH_FN = new WeakMap();
// Vue de la table propre à une feuille : les noms de plusieurs lettres de l'exercice deviennent des identifiants.
const WITH_IDS = new WeakMap();
export function withIdentifiers(table, names) {
  const list = [...new Set(names)]
    .filter((n) => n && !table.lex.names[n])
    .sort((a, b) => b.length - a.length || (a < b ? -1 : 1));
  if (!list.length) return table;
  let byKey = WITH_IDS.get(table);
  if (!byKey) WITH_IDS.set(table, (byKey = new Map()));
  const key = list.join('\u0000');
  if (byKey.has(key)) return byKey.get(key);
  const t = { ...table, lex: { ...table.lex, modelIds: [...(table.lex.modelIds ?? []), ...list] } };
  byKey.set(key, t);
  return t;
}

export function withFunctions(table, names) {
  const list = [...new Set(names)].filter((n) => n && !table.syntax.functions[n]).sort();
  if (!list.length) return table;
  let byKey = WITH_FN.get(table);
  if (!byKey) WITH_FN.set(table, (byKey = new Map()));
  const key = list.join(',');
  if (byKey.has(key)) return byKey.get(key);
  const names2 = { ...table.lex.names };
  const functions = { ...table.syntax.functions };
  for (const n of list) {
    names2[n] = 'function';
    functions[n] = { node: 'Call', latex: n, eval: 'abstract', pack: 'exercise' };
  }
  const t = {
    ...table,
    lex: {
      ...table.lex,
      names: names2,
      nameList: Object.keys(names2).sort((a, b) => b.length - a.length),
    },
    syntax: { ...table.syntax, functions },
  };
  byKey.set(key, t);
  return t;
}

// Grandeurs de l'exercice qui portent le nom d'une constante (« e » = écart de graduations, exposant…) :
// une table dérivée où ce nom redevient une lettre ordinaire. Mise en cache par table et par noms.
const WITHOUT_CONST = new WeakMap();
export function withoutConstants(table, names) {
  const list = [...new Set(names)].filter((n) => n && table.syntax.constants[n]).sort();
  if (!list.length) return table;
  let byKey = WITHOUT_CONST.get(table);
  if (!byKey) WITHOUT_CONST.set(table, (byKey = new Map()));
  const key = list.join(',');
  if (byKey.has(key)) return byKey.get(key);
  const constants = { ...table.syntax.constants };
  const names2 = { ...table.lex.names };
  for (const n of list) {
    delete constants[n];
    if (names2[n] === 'constant') delete names2[n];
  }
  const t = {
    ...table,
    lex: {
      ...table.lex,
      names: names2,
      nameList: Object.keys(names2).sort((a, b) => b.length - a.length),
    },
    syntax: { ...table.syntax, constants },
  };
  byKey.set(key, t);
  return t;
}
